import { Dayjs } from "dayjs"
import {
  EnrolmentBrokerType,
  EnrolmentMappedStatus,
  EnrolmentStatusType,
  IEnrolment,
  IEnrolmentAgreement,
  IEnrolmentContact,
  IEnrolmentPaymentDetails,
} from "@/models/operations/enrolment/enrolment.types"
import { IAddress } from "@/models/address/address.types"
import { errorReporter } from "@/services/ErrorService"
import { Status } from "@/types/Status"
import {
  EnrolmentStatus,
  EnrolmentStatusDetailsMap,
  getBrokerLabel,
} from "@/constants/enrolment"
import { capitalize } from "@/helpers/stringHelpers"
import { TableLink } from "@/types/TableCell"

export class Enrolment implements IEnrolment {
  public brokerLabel: string

  constructor(
    public id: number,
    public broker: EnrolmentBrokerType,
    public submissionStatus: EnrolmentStatusType,
    public email: string,
    public postcode: string,
    public coreAccountId: number | null,
    public accountNumber: string | null,
    public customerNumber: string | null,
    public enrolmentFailureMessage: string | null,
    public thirdPartyReference: string | null,
    public createdAt: Dayjs,
    public mpan: string | null,
    public mprn: string | null,
    public supplyAddress: IAddress | null,
    public contactAddress: IAddress | null,
    public referralCode: string | null,
    public marketingOptIn: boolean,
    public requestedPsr: boolean,
    public agreements: IEnrolmentAgreement[],
    public contact: IEnrolmentContact,
    public paymentDetails: IEnrolmentPaymentDetails,
    public communicationsSent: boolean,
    public retryCount: number,
  ) {
    this.brokerLabel = getBrokerLabel(this.broker)
  }

  get customerName() {
    return `${this.contact.firstName} ${this.contact.lastName}`
  }

  get statusExplanation() {
    return (
      EnrolmentStatusDetailsMap[this.submissionStatus]?.long ?? "Unknown Error"
    )
  }

  get accountPath(): TableLink | null {
    if (!this.accountNumber) return null

    return {
      text: this.accountNumber,
      path: `/customer/${this.accountNumber}`,
    }
  }

  get mappedStatus(): EnrolmentMappedStatus {
    const status = this.submissionStatus
    const isCompleted =
      status === EnrolmentStatus.DONE ||
      status === EnrolmentStatus.REGISTERED_IN_JUNIFER ||
      status === EnrolmentStatus.FAILED_TO_APPLY_VOUCHER ||
      status === EnrolmentStatus.FAILED_TO_CREATE_CORE_ACCOUNTS ||
      status === EnrolmentStatus.FAILED_TO_SEND_PASSWORD_RESET_EMAIL ||
      status === EnrolmentStatus.FAILED_TO_CREATE_USER ||
      status === EnrolmentStatus.FAILED_TO_SEND_EMAIL
    const isPending =
      status === EnrolmentStatus.PROCESSING ||
      status === EnrolmentStatus.PENDING

    if (!EnrolmentStatusDetailsMap[status]) {
      errorReporter.report(
        new Error("Unknown enrolment status", { cause: status }),
      )
    }

    let statusObj
    const statusText = EnrolmentStatusDetailsMap[status]?.short
    if (isCompleted) {
      statusObj = new Status("normal", capitalize(statusText))
    } else if (isPending) {
      statusObj = new Status("caution", capitalize(statusText))
    } else {
      statusObj = new Status(
        "critical",
        capitalize(statusText || "Unknown Error"),
      )
    }

    return {
      value: this.submissionStatus,
      explanation: this.statusExplanation,
      status: statusObj,
    }
  }
}
